export type Severity = "high" | "medium" | "low";

export type Finding = {
  category: "placeholder" | "instruction_leak" | "numeric" | "structure";
  severity: Severity;
  label: string;
  detail: string;
};

export type AnalysisResult = {
  findings: Finding[];
  docWordCount: number;
  embWordCount: number;
};

function stripHtml(html: string): string {
  return new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
}

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function countSubstring(text: string, sub: string): number {
  let count = 0;
  let pos = 0;
  while ((pos = text.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
  return count;
}

const INSTRUCTION_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /GENERATION RULES/i,              label: "Generation rules instruction" },
  { re: /DO NOT INCLUDE/i,                label: "Template exclusion instruction" },
  { re: /INSERT (?:HERE|CLIENT|ADVISER)/i, label: "Insert placeholder instruction" },
  { re: /\[NOTE:/i,                        label: "Template note marker" },
  { re: /\[INSTRUCTION/i,                  label: "Instruction marker" },
  { re: /POPULATE THIS/i,                  label: "Populate instruction" },
  { re: /READ BEFORE/i,                    label: "Template instruction header" },
];

function checkInstructionLeakage(embText: string): Finding[] {
  const findings: Finding[] = [];
  const lines = embText.split(/\n/);

  for (const { re, label } of INSTRUCTION_PATTERNS) {
    if (!re.test(embText)) continue;
    const matchLine = lines.find((l) => re.test(l))?.trim() ?? "";
    findings.push({
      category: "instruction_leak",
      severity: "high",
      label,
      detail: matchLine.length > 120 ? matchLine.slice(0, 120) + "..." : matchLine,
    });
  }

  // Heuristic: lines that are mostly uppercase (likely an instruction row)
  const flaggedDetails = new Set(findings.map((f) => f.detail.slice(0, 20)));
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.length < 30 || !trimmed.includes(" ")) continue;
    const letters = trimmed.replace(/[^a-zA-Z]/g, "");
    if (letters.length === 0) continue;
    const upperPct = trimmed.replace(/[^A-Z]/g, "").length / letters.length;
    if (upperPct > 0.8 && !flaggedDetails.has(trimmed.slice(0, 20))) {
      const detail = trimmed.length > 120 ? trimmed.slice(0, 120) + "..." : trimmed;
      findings.push({
        category: "instruction_leak",
        severity: "high",
        label: "Possible instruction text in output",
        detail,
      });
      flaggedDetails.add(trimmed.slice(0, 20));
    }
  }

  return findings;
}

function checkPlaceholders(docHtml: string, embHtml: string): Finding[] {
  const findings: Finding[] = [];
  const docTbc = countSubstring(docHtml, "[TBC");
  const embTbc = countSubstring(embHtml, "[TBC");
  const docReq = countSubstring(docHtml, "[REQUIRED");
  const embReq = countSubstring(embHtml, "[REQUIRED");

  if (embTbc > 0 || docTbc > 0) {
    const diff = embTbc - docTbc;
    findings.push({
      category: "placeholder",
      severity: diff > 0 ? "high" : diff < 0 ? "medium" : "low",
      label: `[TBC]: embedding ${embTbc}, document ${docTbc} (diff ${diff > 0 ? "+" : ""}${diff})`,
      detail: `Embedding has ${embTbc} unfilled [TBC] tags  /  Document has ${docTbc}`,
    });
  }
  if (embReq > 0 || docReq > 0) {
    const diff = embReq - docReq;
    findings.push({
      category: "placeholder",
      severity: diff > 0 ? "high" : diff < 0 ? "medium" : "low",
      label: `[REQUIRED]: embedding ${embReq}, document ${docReq} (diff ${diff > 0 ? "+" : ""}${diff})`,
      detail: `Embedding has ${embReq} unfilled [REQUIRED] tags  /  Document has ${docReq}`,
    });
  }
  return findings;
}

function normalizeLabel(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();
}

function normalizeValue(s: string): string {
  return s.toLowerCase().replace(/,/g, "").replace(/\s+/g, " ").trim();
}

function toTitleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function extractKeyValuePairs(html: string): Map<string, string> {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const pairs = new Map<string, string>(); // normalised label → original value

  // From tables: first cell = label, subsequent cells = value
  for (const row of doc.querySelectorAll("tr")) {
    const cells = [...row.querySelectorAll("td, th")].map((c) => c.textContent?.trim() ?? "");
    if (cells.length < 2) continue;
    const rawLabel = cells[0] ?? "";
    const rawValue = cells.slice(1).join(" ").trim();
    if (
      rawLabel.length < 3 || rawLabel.length > 100 ||
      rawValue.length < 1 || rawValue.length > 200 ||
      !/[$£€\d%]/.test(rawValue)
    ) continue;
    pairs.set(normalizeLabel(rawLabel), rawValue);
  }

  // From text: "Label: Value" patterns where value contains a number
  const text = doc.body.textContent ?? "";
  for (const line of text.split(/\n/)) {
    const m = line.match(/^([A-Za-z][^:\n]{2,60}):\s*(.{1,150})$/);
    if (!m) continue;
    const rawLabel = (m[1] ?? "").trim();
    const rawValue = (m[2] ?? "").trim();
    if (!rawLabel || !rawValue) continue;
    if (!/[$£€\d%]/.test(rawValue)) continue;
    if (rawValue.split(/[.!?]/).length > 2) continue; // skip sentences
    const normLabel = normalizeLabel(rawLabel);
    if (!pairs.has(normLabel)) pairs.set(normLabel, rawValue);
  }

  return pairs;
}

function checkKeyValueDiff(docHtml: string, embHtml: string): Finding[] {
  const docPairs = extractKeyValuePairs(docHtml);
  const embPairs = extractKeyValuePairs(embHtml);

  const findings: Finding[] = [];
  for (const [normLabel, docVal] of docPairs) {
    if (findings.length >= 20) break;
    const embVal = embPairs.get(normLabel) ?? null;
    if (embVal === null) {
      findings.push({
        category: "numeric",
        severity: "high",
        label: `${toTitleCase(normLabel)}: missing from embedding`,
        detail: `Document: ${docVal}`,
      });
    } else if (normalizeValue(embVal) !== normalizeValue(docVal)) {
      findings.push({
        category: "numeric",
        severity: "high",
        label: `${toTitleCase(normLabel)}: ${docVal} vs ${embVal}`,
        detail: "Value differs between document output and embedding",
      });
    }
  }
  return findings;
}

function extractHeadings(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const results: string[] = [];
  for (const el of doc.querySelectorAll("h1, h2, h3")) {
    const text = el.textContent?.trim().toLowerCase() ?? "";
    if (text.length > 2) results.push(text);
  }
  return results;
}

function checkStructure(docHtml: string, embHtml: string): Finding[] {
  const docHeadings = extractHeadings(docHtml);
  const embHeadings = new Set(extractHeadings(embHtml));
  return docHeadings
    .filter((h) => !embHeadings.has(h))
    .map((h) => ({
      category: "structure" as const,
      severity: "medium" as const,
      label: "Section missing from embedding",
      detail: `"${h.length > 80 ? h.slice(0, 80) + "..." : h}"`,
    }));
}

export function runAnalysis(docHtml: string, embHtml: string): AnalysisResult {
  const docText = stripHtml(docHtml);
  const embText = stripHtml(embHtml);
  const docWords = wordCount(docText);
  const embWords = wordCount(embText);

  const findings: Finding[] = [
    ...checkInstructionLeakage(embText),
    ...checkPlaceholders(docHtml, embHtml),
    ...checkKeyValueDiff(docHtml, embHtml),
    ...checkStructure(docHtml, embHtml),
  ];

  return { findings, docWordCount: docWords, embWordCount: embWords };
}
